import { createContext, useContext, useState, useCallback, useRef } from "react";
import { FiCheckCircle, FiAlertCircle, FiInfo } from "react-icons/fi";
import { useI18n } from "./I18nContext";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = useCallback((message, type = "success", duration = 4000, onClick = null) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type, onClick }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "16px",
        left: "8px",
        right: "8px",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column-reverse",
        gap: "8px",
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function DismissButton({ color, onDismiss }) {
  const { t } = useI18n();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onDismiss}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "absolute",
        top: "6px",
        right: "6px",
        background: hovered ? "rgba(0,0,0,0.1)" : "none",
        border: "none",
        borderRadius: "4px",
        color,
        cursor: "pointer",
        fontSize: "18px",
        lineHeight: "1",
        width: "24px",
        height: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0",
        transition: "background 0.15s",
      }}
      title={t("Schließen")}
    >
      ×
    </button>
  );
}

function ToastItem({ toast, onDismiss }) {
  const isSuccess = toast.type === "success";
  const isError = toast.type === "error";
  const isInfo = toast.type === "info";

  const bg = isSuccess ? "#d4edda" : isError ? "#f8d7da" : isInfo ? "#cce5ff" : "#fff3cd";
  const border = isSuccess ? "#c3e6cb" : isError ? "#f5c6cb" : isInfo ? "#b8daff" : "#ffeaa7";
  const color = isSuccess ? "#155724" : isError ? "#721c24" : isInfo ? "#004085" : "#856404";
  const Icon = isSuccess ? FiCheckCircle : isError ? FiAlertCircle : FiInfo;

  const handleClick = toast.onClick
    ? () => { onDismiss(toast.id); toast.onClick(); }
    : null;

  return (
    <div
      onClick={handleClick}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        color,
        borderRadius: "8px",
        padding: "10px 36px 10px 14px",
        fontSize: "13px",
        fontWeight: "600",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        position: "relative",
        pointerEvents: "all",
        animation: "slideUp 0.2s ease-out",
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        cursor: handleClick ? "pointer" : "default",
      }}
    >
      <Icon size={15} style={{ marginTop: "1px", flexShrink: 0 }} />
      <span>{toast.message}</span>
      <DismissButton color={color} onDismiss={() => onDismiss(toast.id)} />
    </div>
  );
}
